/**
 * What the Routing section says, worked out without React. org-admin#98.
 *
 * fleet-api moves the `CI_RUNNER_LABEL` organisation variable between your
 * runners, Blacksmith and GitHub-hosted runners (fleet-api#21), and takes a
 * manual switch from here. The wording lives in this file because it is the
 * part that can mislead: a screen that says "Automatic" while routing is paused
 * by hand is how a failover that never comes gets trusted.
 */
import type { BoxVerb, GitHubMinutes, Routing, RoutingAuditRow } from './fleet-api.js';
import { verbLabel } from './fleet-boxes.js';
import { usd, type Tone } from './fleet-view.js';
import { count } from './format.js';

export type RunnerTarget = 'fleet' | 'blacksmith' | 'github';

const percent = (part: number, whole: number) => Math.min(100, Math.max(0, (part / Math.max(1, whole)) * 100));

/**
 * Blacksmith's bar (Jon, 14 Sep): scaled to the free minutes, green up to the
 * trip line, amber past it, with a marker on the line. Minutes past the free
 * allowance cannot be drawn on a bar that ends there, so they are counted.
 */
export function blacksmithMeter(b: Routing['blacksmith']): { okPct: number; warnPct: number; markerPct: number; billedMin: number } {
  const trip = Math.min(b.trip_min, b.free_min);
  const okPct = percent(Math.min(b.used_min, trip), b.free_min);
  return {
    okPct,
    warnPct: percent(b.used_min, b.free_min) - okPct,
    markerPct: percent(trip, b.free_min),
    billedMin: Math.max(0, b.used_min - b.free_min),
  };
}

/**
 * GitHub-hosted's bar: scaled to the most Actions can use this month (the
 * included minutes plus what a stopping budget buys), or to the included
 * minutes when fleet-api does not know of one. No marker: the budget stops
 * Actions at the end of the bar, so there is no line to cross before it.
 */
export function githubMeter(usedMin: number | null, limit: Routing['github_limit']): { maxMin: number; pct: number; capped: boolean } {
  const maxMin = limit.cap_min ?? limit.included_min;
  return { maxMin, pct: usedMin === null ? 0 : percent(usedMin, maxMin), capped: limit.cap_min !== null };
}

/** In chain order, with the names every CI fleet panel uses. */
export const TARGETS: readonly { target: RunnerTarget; name: string; phrase: string }[] = [
  { target: 'fleet', name: 'Your runners', phrase: 'your runners' },
  { target: 'blacksmith', name: 'Blacksmith', phrase: 'Blacksmith' },
  { target: 'github', name: 'GitHub-hosted', phrase: 'GitHub-hosted runners' },
];

/** Which target a label is, by the chain fleet-api reports. Null for a label outside it. */
export function targetOf(label: string | null | undefined, chain: Routing['chain']): RunnerTarget | null {
  if (!label) return null;
  return TARGETS.find((t) => chain[t.target] === label)?.target ?? null;
}

/** A label as a person would say it: the target's name, or the label itself when it is not one. */
export function labelName(label: string | null | undefined, chain: Routing['chain']): string {
  if (label === null || label === undefined) return 'not read yet';
  const target = targetOf(label, chain);
  return target ? TARGETS.find((t) => t.target === target)!.name : label === '' ? 'an empty label' : label;
}

const minutesOf = (s: number) => `${count(Math.round(s / 60))} min`;

/** The headline: what automatic routing is doing right now, in the order that matters. */
export function routingStatus(r: Routing): { tone: Tone; label: string; detail: string | null } {
  if (r.problem) return { tone: 'danger', label: 'Routing misconfigured', detail: r.problem };
  if (r.last_routing_error) return { tone: 'danger', label: 'Routing failing', detail: r.last_routing_error };
  if (r.mode === 'off') {
    return { tone: 'muted', label: 'Automatic routing off', detail: 'CI stays wherever it is set until routing is turned back on.' };
  }
  if (r.state.paused_reason) return { tone: 'warn', label: 'Automatic routing paused', detail: r.state.paused_reason };
  if (r.state.label === null) {
    return { tone: 'muted', label: 'Not read yet', detail: 'fleet-api reads the label on its next tick, within a minute.' };
  }
  const target = targetOf(r.state.label, r.chain);
  if (r.mode === 'dry_run') {
    return {
      tone: 'muted',
      label: 'Dry run',
      detail: 'Routing records what it would do in the history below, and does not move CI on its own.',
    };
  }
  if (target !== 'fleet') {
    return {
      tone: 'warn',
      label: `Failed over to ${labelName(r.state.label, r.chain)}`,
      detail: `Returns to your runners once they have been up for ${minutesOf(r.failback_after_s)}.`,
    };
  }
  return {
    tone: 'good',
    label: 'Automatic',
    detail: `Moves CI to Blacksmith if a job waits ${minutesOf(r.failover_after_s)} for your runners with none of them starting work, and re-runs the jobs left waiting.`,
  };
}

/** The Result column of the routing history. */
export function auditView(row: RoutingAuditRow): { label: string; tone: 'neutral' | 'good' | 'warn' | 'danger' } {
  switch (row.action) {
    case 'switch':
      return row.verified === false ? { label: 'Switched, not confirmed', tone: 'warn' } : { label: 'Switched', tone: 'good' };
    case 'would_switch':
      return { label: 'Would switch', tone: 'neutral' };
    case 'external_change':
      return { label: row.from_label === null ? 'First reading' : 'Changed outside fleet-api', tone: 'neutral' };
    case 'refused':
      return { label: 'Refused', tone: 'warn' };
    case 'error':
      return { label: 'Failed', tone: 'danger' };
    // A run a failover left waiting on your runners, cancelled and re-run on the new label (fleet-api#38).
    case 'cancel':
      return { label: 'Cancelled stuck run', tone: 'neutral' };
    case 'rerun':
      return row.verified === true
        ? { label: 'Re-ran stuck run', tone: 'good' }
        : row.verified === false
          ? { label: 'Re-run not confirmed', tone: 'warn' }
          : { label: 'Not re-run', tone: 'neutral' };
    // A box action (fleet-api#24, org-admin#99). The result's `verified` is true
    // for success, false for a failure, an expiry or a timeout (the reason says
    // which), and null for a cancel.
    case 'action_requested':
      return { label: 'Box action requested', tone: 'neutral' };
    case 'action_claimed':
      return { label: 'Picked up by the box', tone: 'neutral' };
    case 'action_result':
      return row.verified === true
        ? { label: 'Box action succeeded', tone: 'good' }
        : row.verified === false
          ? { label: 'Box action failed', tone: 'danger' }
          : { label: 'Box action cancelled', tone: 'neutral' };
    default:
      return { label: row.action, tone: 'neutral' };
  }
}

/** The box and verb an `action_*` row is about, from its `inputs`, or null for any other row. */
export function boxActionOf(row: RoutingAuditRow): { box: string; verb: BoxVerb; target: string | null } | null {
  if (!row.action.startsWith('action_')) return null;
  const inputs = (typeof row.inputs === 'object' && row.inputs !== null ? row.inputs : {}) as { box?: unknown; verb?: unknown; target?: unknown };
  if (typeof inputs.box !== 'string' || typeof inputs.verb !== 'string') return null;
  return { box: inputs.box, verb: inputs.verb as BoxVerb, target: typeof inputs.target === 'string' ? inputs.target : null };
}

/**
 * A row about CI_HOSTED_LABEL, the low-churn repos' label (fleet-api#49), rather
 * than CI_RUNNER_LABEL. Both move between the same Blacksmith and GitHub-hosted
 * labels, so without this a hosted-tier move reads exactly like a fleet failover.
 */
export function isHostedRow(row: RoutingAuditRow): boolean {
  return typeof row.inputs === 'object' && row.inputs !== null && (row.inputs as { variable?: unknown }).variable === 'CI_HOSTED_LABEL';
}

/** The Change column: the labels a switch moved between, or the box and action. */
export function changeText(row: RoutingAuditRow, chain: Routing['chain']): string {
  const action = boxActionOf(row);
  if (action) return `${action.box}: ${verbLabel(action.verb, action.target)}`;
  const labels =
    row.from_label === null && row.to_label === null
      ? 'None'
      : row.from_label === null
        ? labelName(row.to_label, chain)
        : `${labelName(row.from_label, chain)} to ${labelName(row.to_label, chain)}`;
  return isHostedRow(row) ? `Hosted tier: ${labels}` : labels;
}

const resetFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * Where the low-churn repos run (fleet-api#49): which card carries their tag,
 * its name, and what happens next, in a few words. Null when fleet-api does not
 * report the hosted tier. fleet-api moves their label to GitHub-hosted at the
 * Blacksmith trip line, and back when the month resets.
 */
export function hostedTier(r: Routing): { target: RunnerTarget | null; name: string; next: string } | null {
  if (!r.hosted) return null;
  // Before the first crossing fleet-api has stored no label, and the workflows default to Blacksmith.
  const label = r.hosted.label ?? r.chain.blacksmith;
  const target = targetOf(label, r.chain);
  const reset = new Date(`${r.as_of.slice(0, 7)}-01T00:00:00Z`);
  reset.setUTCMonth(reset.getUTCMonth() + 1);
  return {
    target,
    name: labelName(label, r.chain),
    next:
      target === 'github'
        ? `back to Blacksmith on ${resetFmt.format(reset)}`
        : `moves to GitHub-hosted at ${count(r.blacksmith.trip_min)} Blacksmith min`,
  };
}

/** The figure under Blacksmith's bar: minutes billed past the free allowance, or the free minutes left. */
export function blacksmithNote(b: Routing['blacksmith']): { text: string; warn: boolean } {
  const billed = b.used_min - b.free_min;
  return billed > 0
    ? { text: `${count(billed)} min billed`, warn: true }
    : { text: `${count(-billed)} min free left`, warn: b.used_min >= b.trip_min };
}

/** The figure under GitHub-hosted's bar: what has been paid, against the budget when one stops Actions. */
export function githubNote(gh: GitHubMinutes, limit: Routing['github_limit']): string {
  const budget = limit.cap_min !== null && limit.budget_usd !== null ? ` of ${usd(limit.budget_usd)} budget` : '';
  return `${usd(gh.net_usd)} paid${budget}`;
}

/**
 * Who made a change. fleet-api records an admin by id only (the token carries no
 * name), so the one admin this screen can name is the one looking at it. A box
 * claiming or finishing its own action is recorded by the box's name, passed as
 * `box` so it is not read as an admin id.
 */
export function actorName(actor: string, currentUserId?: string | null, box?: string | null): string {
  if (actor === 'fleet-api') return 'Automatic';
  if (actor === 'github') return 'Outside fleet-api';
  if (box && actor === box) return box;
  if (currentUserId && actor === currentUserId) return 'You';
  return `Admin ${actor.slice(0, 8)}`;
}

/** What the confirmation says before a switch, so nobody learns what it does by doing it. */
export function switchCopy(to: RunnerTarget, r: Routing): { title: string; points: string[]; button: string; done: string } {
  const { phrase } = TARGETS.find((t) => t.target === to)!;
  const points = [
    `Every auth and keyodo job queued from now on runs on ${phrase}.`,
    'Jobs already queued stay where they were sent. Re-run any that are stuck.',
  ];

  if (to === 'fleet') {
    points.push(
      r.mode === 'auto'
        ? 'Automatic routing resumes, so CI moves to Blacksmith again if your runners stop taking jobs.'
        : `Automatic routing is ${r.mode === 'off' ? 'off' : 'in dry run'}, so nothing moves CI after this on its own.`,
    );
  } else {
    points.push(
      r.mode === 'auto'
        ? 'Automatic routing pauses until CI is set back to your runners, so it will not undo this.'
        : `Automatic routing is ${r.mode === 'off' ? 'off' : 'in dry run'}, so CI stays here until someone moves it.`,
    );
  }
  if (to === 'blacksmith' && r.blacksmith.used_min >= r.blacksmith.trip_min) {
    points.push(
      `Blacksmith has used ${count(r.blacksmith.used_min)} minutes this month, past the ${count(r.blacksmith.trip_min)} where automatic routing would choose GitHub-hosted instead.`,
    );
  }

  return {
    title: `Move CI to ${phrase}?`,
    points,
    button: `Move CI to ${phrase}`,
    done: `Moved CI to ${phrase}.`,
  };
}
